// The "Registra ricevuta di radiazione" dialog and the receipt step that closes the PRA case
// (Stage 2 slice 7). Plain module with no Vue, so it runs under `node --test`. Labels are i18n keys;
// the API checks everything again (number, dates, file) and is the only one that confirms the case.

import { todayRome } from "./draftForm.mjs";
import { checkUpload } from "./dossierFiles.mjs";

// Same limit as the API (pra/manualConnector.js), like the CRD number.
export const RECEIPT_NUMBER_MAX = 100;

export function emptyReceiptForm() {
  return { number: "", date: "", file: null };
}

// The receipt date goes from the request date to today in Europe/Rome (both included).
export function receiptDateLimits({ requestDate, now = new Date() }) {
  return { min: requestDate || "", max: todayRome(now) };
}

// { field: reason } for what blocks the save; {} when it can be sent. "required" only disables the
// button; the other reasons show under the field. Number, date and file are all required.
export function receiptFormErrors(form, limits) {
  const errors = {};
  const number = String((form && form.number) || "").trim();
  if (!number) errors.number = "required";
  else if (number.length > RECEIPT_NUMBER_MAX) errors.number = "too_long";
  const date = (form && form.date) || "";
  if (!date) errors.date = "required";
  else if (limits.min && date < limits.min) errors.date = "before_request";
  else if (limits.max && date > limits.max) errors.date = "in_future";
  if (!form || !form.file) errors.file = "required";
  else {
    const problem = checkUpload(form.file, "pra");
    if (problem) errors.file = problem;
  }
  return errors;
}

// One commandId per receipt: a retry of the same input reuses it, so a double click records it once.
export function receiptSubmitKey(form) {
  const file = form.file || {};
  return [
    String(form.number).trim(),
    form.date,
    file.name,
    file.size,
    file.lastModified,
  ].join("|");
}

// The "Registra ricevuta di radiazione" button: shown while the case waits for the receipt,
// disabled on hold. A confirmed case has no next step, so no button.
export function receiptAction(view) {
  const praCase = view && view.praCase;
  if (!praCase || view.nextStep !== "receipt") return null;
  return { disabled: Boolean(praCase.activeHold) };
}

// A failed receipt save -> the i18n key of its message, or null to show the API's errorMsg. The
// messages that read the same for every step are the CRD's.
const RECEIPT_ERROR_CODES = [
  "date_order",
  "date_in_future",
  "file_required",
  "step_out_of_order",
];
const SHARED_ERROR_CODES = [
  "unsupported_type",
  "already_confirmed",
  "case_on_hold",
];

export function receiptErrorKey(err) {
  const res = err && err.response;
  if (!res) return null;
  // A proxy in front of the API may answer 413 without our JSON body.
  if (res.status === 413) return "acceptance.pra.crd.errors.too_large";
  const code = res.data && res.data.code;
  if (RECEIPT_ERROR_CODES.includes(code))
    return `acceptance.pra.receipt.errors.${code}`;
  if (SHARED_ERROR_CODES.includes(code))
    return `acceptance.pra.crd.errors.${code}`;
  return null;
}

// The green banner of a confirmed case: { date (YYYY-MM-DD), number } from the receipt, or null
// while the case is open.
export function confirmedBanner(praCase) {
  if (!praCase || praCase.state !== "confirmed") return null;
  const receipt = praCase.receipt || {};
  return { date: receipt.date || "", number: receipt.number || "" };
}
